import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

import { StepQuantity } from 'interfaces/auth';

type PrevStep = (step?: StepQuantity) => void;

const resetPasswordContext = createContext<{
  step: number;
  confirmationCodeError: string;
  nextStep: () => void;
  prevStep: PrevStep;
  setCodeError: (error: string) => void;
}>({
  step: 0,
  confirmationCodeError: '',
  nextStep: () => {},
  prevStep: () => {},
  setCodeError: () => {},
});

export const useResetPasswordContext = () => {
  return useContext(resetPasswordContext);
};

export const ResetPasswordContextProvider = ({ children }: PropsWithChildren<unknown>) => {
  const [resetPasswordContextState, setResetPasswordContextState] = useState<{
    step: number;
  }>({
    step: 0,
  });
  const [confirmationCodeError, setConfirmationCodeError] = useState('');

  const nextStep = useCallback(() => {
    setResetPasswordContextState((prev) => ({
      step: prev.step + 1,
    }));
  }, []);

  const prevStep = useCallback((step = StepQuantity.ONE) => {
    setResetPasswordContextState((prev) => ({
      step: prev.step - step,
    }));
  }, []);

  const setCodeError = useCallback((error: string) => {
    setConfirmationCodeError(error);
  }, []);

  return (
    <resetPasswordContext.Provider
      value={useMemo(
        () => ({ ...resetPasswordContextState, nextStep, prevStep, confirmationCodeError, setCodeError }),
        [resetPasswordContextState, confirmationCodeError, nextStep, prevStep, setCodeError],
      )}
    >
      {children}
    </resetPasswordContext.Provider>
  );
};
